import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { Upload, Sparkles, Scan, X, ImageIcon, Loader2, Camera, ImagePlus, AlertTriangle, Clock } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  analyzeChart,
  getAnalystQuota,
  getAnalystHistory,
  type AnalystReport,
  type AnalysisHistoryRow,
} from "@/lib/analyst.functions";
import { authedFetch } from "@/lib/authed-fetch";
import { ReportView, ReportSkeleton } from "@/components/analyst/report-view";
import { SignalCard } from "@/components/analyst/signal-card";
import { HistoryPanel } from "@/components/analyst/history-panel";

export const Route = createFileRoute("/_authenticated/dashboard/analyst")({
  head: () => ({
    meta: [
      { title: "AI SMC Analyst | XAU Pro Academy" },
      {
        name: "description",
        content:
          "Upload a chart and get an institutional Smart Money Concept analysis in seconds with the XAU Pro AI Analyst.",
      },
    ],
  }),
  loader: async () => {
    const [quota, history] = await Promise.all([
      getAnalystQuota({ fetch: authedFetch }),
      getAnalystHistory({ fetch: authedFetch }),
    ]);
    return { quota, history };
  },
  component: AnalystPage,
});

function normalizeNum(value: unknown, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

type QuotaState = {
  remaining: number;
  used: number;
  plan_limit: number;
  plan_name: string;
  plan_key: string;
  period: "day" | "week";
};

const DEFAULT_QUOTA: QuotaState = {
  remaining: 1,
  used: 0,
  plan_limit: 1,
  plan_name: "Foundations Member",
  plan_key: "free",
  period: "week",
};

function toQuotaState(input: unknown): QuotaState {
  const q = (input ?? {}) as Partial<QuotaState>;
  return {
    remaining: normalizeNum(q.remaining, DEFAULT_QUOTA.remaining),
    used: normalizeNum(q.used, DEFAULT_QUOTA.used),
    plan_limit: normalizeNum(q.plan_limit, DEFAULT_QUOTA.plan_limit),
    plan_name: typeof q.plan_name === "string" ? q.plan_name : DEFAULT_QUOTA.plan_name,
    plan_key: typeof q.plan_key === "string" ? q.plan_key : DEFAULT_QUOTA.plan_key,
    period: q.period === "day" || q.period === "week" ? q.period : DEFAULT_QUOTA.period,
  };
}

const MAX_DIMENSION = 1600;
const COMPRESS_IF_LARGER_THAN = 1.5 * 1024 * 1024; // 1.5 MB
const HARD_MAX_BYTES = 10 * 1024 * 1024; // 10 MB upload cap

type EncodedImage = {
  base64: string;
  mediaType: "image/png" | "image/jpeg" | "image/webp";
};

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

function dataUrlToBase64(dataUrl: string): string {
  const [, base64] = dataUrl.split(",");
  return base64;
}

async function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not decode image"));
    img.src = dataUrl;
  });
}

async function encodeForUpload(file: File): Promise<EncodedImage> {
  const mt = file.type;
  const supported =
    mt === "image/png" || mt === "image/jpeg" || mt === "image/webp";
  if (!supported) throw new Error("Unsupported image type. Use PNG, JPG, or WEBP.");

  const dataUrl = await readAsDataUrl(file);

  // Fast path: already small enough — send as-is.
  if (file.size <= COMPRESS_IF_LARGER_THAN) {
    return { base64: dataUrlToBase64(dataUrl), mediaType: mt };
  }

  // Otherwise downscale + re-encode as JPEG.
  const img = await loadImage(dataUrl);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    // Canvas unavailable — fall back to original.
    return { base64: dataUrlToBase64(dataUrl), mediaType: mt };
  }
  ctx.drawImage(img, 0, 0, w, h);
  const compressed = canvas.toDataURL("image/jpeg", 0.85);
  return { base64: dataUrlToBase64(compressed), mediaType: "image/jpeg" };
}

/** Next reset moment (ms epoch). Daily and weekly buckets both reset at the next 00:00 UTC boundary. */
function nextResetAt(_period: "day" | "week"): number {
  const now = new Date();
  return Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0,
    0,
    0,
    0,
  );
}

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`;
  return `${pad(m)}m ${pad(s)}s`;
}

function useCountdown(targetMs: number, active: boolean, onZero?: () => void) {
  const [now, setNow] = useState(() => Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    if (!active) return;
    firedRef.current = false;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active, targetMs]);

  const remaining = Math.max(0, targetMs - now);

  useEffect(() => {
    if (active && remaining === 0 && !firedRef.current) {
      firedRef.current = true;
      onZero?.();
    }
  }, [active, remaining, onZero]);

  return remaining;
}
function AnalystPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const initial = Route.useLoaderData();

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [chartDataUrl, setChartDataUrl] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [report, setReport] = useState<AnalystReport | null>(null);
  const [quota, setQuota] = useState<QuotaState>(toQuotaState(initial?.quota));
  const [history, setHistory] = useState<AnalysisHistoryRow[]>(initial?.history ?? []);
  const [analyzedForFile, setAnalyzedForFile] = useState(false);
  const [autoRun, setAutoRun] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  const quotaExhausted = quota.remaining <= 0;
  const periodLabel = quota.period === "week" ? "Weekly" : "Daily";
  const periodWordLower = quota.period === "week" ? "week" : "day";
  const isElite = quota.plan_key === "elite";
  const limitHeadline = isElite
    ? `${periodLabel} limit reached for your ${quota.plan_name} plan.`
    : `${periodLabel} limit reached for your ${quota.plan_name} plan. Upgrade to get more analyses!`;
  // Toast still uses the upgrade-flavored copy when applicable; for elite, only the headline.
  const limitMessage = limitHeadline;

  // Live countdown to next credit reset (next 00:00 UTC). Active only while exhausted.
  const resetAt = useMemo(() => nextResetAt(quota.period), [quota.period, quota.used, quota.remaining]);
  const onResetReached = useCallback(() => {
    router.invalidate();
  }, [router]);
  const remainingMs = useCountdown(resetAt, quotaExhausted, onResetReached);
  const countdownLabel = quota.period === "week" ? "Next credit available in" : "Next reset in";


  const acceptFile = useCallback((f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, or WEBP).");
      return;
    }
    if (f.size > HARD_MAX_BYTES) {
      toast.error("Image is too large (max 10 MB). Please use a smaller screenshot.");
      return;
    }
    setFile(f);
    setReport(null);
    setAnalyzedForFile(false);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Pick up a chart that was dropped on the dashboard's AI widget.
  // The widget stashes the file as a data URL in sessionStorage and routes
  // here, so we materialize it into a real File and seed the picker.
  useEffect(() => {
    const PENDING_KEY = "xaupro:analyst:pendingUpload";
    try {
      const raw = sessionStorage.getItem(PENDING_KEY);
      if (!raw) return;
      sessionStorage.removeItem(PENDING_KEY);
      const stash = JSON.parse(raw) as { name: string; type: string; dataUrl: string; autoAnalyze?: boolean };
      const [, base64] = stash.dataUrl.split(",");
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const file = new File([bytes], stash.name || "chart.png", {
        type: stash.type || "image/png",
      });
      acceptFile(file);
      if (stash.autoAnalyze) setAutoRun(true);
    } catch {
      // Ignore — user can still upload manually.
    }
  }, [acceptFile]);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const f = item.getAsFile();
          if (f) {
            acceptFile(f);
            e.preventDefault();
            break;
          }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [acceptFile]);

  useEffect(() => {
    if (report && !analyzing) {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [report, analyzing]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  };

  const removeFile = () => {
    setFile(null);
    setReport(null);
    setAnalyzedForFile(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setChartDataUrl(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleAnalyze = async () => {
    if (!file) return;
    if (quotaExhausted) {
      toast.error(limitMessage);
      return;
    }

    setAnalyzing(true);
    setReport(null);
    try {
      const { base64, mediaType } = await encodeForUpload(file);
      setChartDataUrl(`data:${mediaType};base64,${base64}`);
      const result = await analyzeChart({
        data: { imageBase64: base64, mediaType },
        fetch: authedFetch,
      });

      if (!result.ok) {
        setQuota(toQuotaState(result.quota));
        if (result.error === "quota_exhausted") {
          toast.error(result.message || limitMessage);
        } else {
          toast.error(result.message || "Could not analyze chart. Please try again.");
        }
        return;
      }

      setReport(result.report);
      setAnalyzedForFile(true);
      setQuota(toQuotaState(result.quota));
      // Optimistic prepend so the History panel updates immediately.
      setHistory((prev) => {
        const optimistic: AnalysisHistoryRow = {
          id: `local-${Date.now()}`,
          created_at: new Date().toISOString(),
          asset: result.report.asset ?? "XAUUSD",
          bias: result.report.bias,
          direction: result.report.trade_setup.direction,
          confidence: result.report.confidence,
          confidence_pct: result.report.confidence_pct,
          key_zone: result.report.key_zone,
          summary: result.report.summary,
          entry_zone: result.report.trade_setup.entry_zone,
          tp: result.report.trade_setup.tp,
          sl: result.report.trade_setup.sl,
          invalidation: result.report.trade_setup.invalidation,
          raw_json_response: result.report,
        };
        return [optimistic, ...prev].slice(0, 5);
      });
      // Refresh loader so server-truth replaces the optimistic row + badge stays accurate.
      router.invalidate();
    } catch (e) {
      console.error(e);
      const msg =
        e instanceof Error && e.message
          ? e.message
          : "Could not analyze chart. Please try again.";
      toast.error(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  // Live chart hand-off from the dashboard starts the scan without a second click.
  useEffect(() => {
    if (!autoRun || !file || analyzing || analyzedForFile) return;
    setAutoRun(false);
    void handleAnalyze();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, file]);

  const handleConvertToSignal = useCallback(() => {
    if (!report) return;
    try {
      const payload = { report, chartDataUrl, createdAt: Date.now() };
      sessionStorage.setItem("xaupro:analyst:pendingSignal", JSON.stringify(payload));
    } catch (e) {
      console.error("Could not stash pending signal", e);
      toast.error("Could not pass analysis to signal form. Please try again.");
      return;
    }
    navigate({ to: "/admin/signals/new" });
  }, [report, chartDataUrl, navigate]);

  return (
    <div className="mx-auto w-full min-w-0 max-w-4xl space-y-8 px-3 py-4 sm:px-4 md:p-8">
      {/* Header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
            <span className="text-gradient-gold">AI SMC Analyst</span>
          </h1>
          <p className="text-sm text-muted-foreground md:text-base">
            Upload your chart and get an institutional analysis in seconds.
          </p>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <Badge variant="outline" className="w-fit border-gold/40 bg-gold-muted/40 text-gold">
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            {`${quota.plan_name} · ${quota.remaining}/${quota.plan_limit} ${quota.period === "week" ? "this week" : "today"}`}
          </Badge>
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {quota.used} {quota.used === 1 ? "analysis" : "analyses"} used this {periodWordLower}
          </span>
        </div>
      </header>

      {/* Upload Card */}
      <Card
        onClick={() => {
          if (file) return;
          // On mobile, don't auto-open file picker on card tap — explicit buttons handle it.
          if (isMobile) return;
          inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`group/drop bento-hover relative flex min-h-[280px] flex-col items-center justify-center gap-4 overflow-hidden rounded-2xl border bg-gradient-to-br from-gold-muted/10 via-card/30 to-card/60 p-6 text-center backdrop-blur-md transition-all duration-300 md:min-h-[360px] md:p-8 ${
          !file ? "animate-gold-glow" : ""
        } ${
          dragging
            ? "border-gold/60 bg-gold-muted/20"
            : "border-gold/20 hover:border-gold/50 hover:bg-gold-muted/10"
        } ${!file && !isMobile ? "cursor-pointer" : "cursor-default"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => acceptFile(e.target.files?.[0] ?? null)}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => acceptFile(e.target.files?.[0] ?? null)}
        />

        {file && previewUrl ? (
          <div className="flex w-full flex-col items-center gap-4">
            <img
              src={previewUrl}
              alt="Selected chart preview"
              className="max-h-72 rounded-lg border border-gold/30 object-contain shadow-gold"
            />
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <ImageIcon className="h-4 w-4 text-gold" />
              <span className="truncate max-w-[220px] sm:max-w-md">{file.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile();
                }}
                disabled={analyzing}
                className="h-7 px-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Upload
              className="h-10 w-10 text-gold animate-upload-pulse group-hover/drop:[animation-play-state:paused]"
              strokeWidth={2}
            />
            <div className="space-y-1.5">
              <h2 className="text-sm font-semibold text-foreground">
                {isMobile ? "Upload your chart" : "Drop your chart, get bias instantly"}
              </h2>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                PNG · JPG · WEBP
              </p>
              {!isMobile && (
                <p className="text-xs text-muted-foreground">
                  paste with{" "}
                  <kbd className="rounded border border-border/60 bg-card/60 px-1.5 py-0.5 text-[10px]">
                    Ctrl + V
                  </kbd>{" "}
                  · or click to browse
                </p>
              )}
            </div>

            {isMobile && (
              <div className="mt-2 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
                <Button
                  type="button"
                  variant="gold"
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    inputRef.current?.click();
                  }}
                  className="w-full sm:w-auto"
                >
                  <ImagePlus className="mr-2 h-5 w-5" />
                  Choose from Gallery
                </Button>
                <Button
                  type="button"
                  variant="outlineGold"
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    cameraRef.current?.click();
                  }}
                  className="w-full sm:w-auto"
                >
                  <Camera className="mr-2 h-5 w-5" />
                  Take a Photo
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Analyze button */}
      <div className="flex flex-col items-center gap-2">
        <Button
          size="lg"
          onClick={handleAnalyze}
          disabled={!file || analyzing || quotaExhausted || analyzedForFile}
          className="w-full bg-gradient-gold text-gold-foreground shadow-gold hover:opacity-90 sm:w-auto sm:min-w-[240px]"
        >
          {analyzing ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Analyzing…
            </>
          ) : analyzedForFile ? (
            <>
              <Scan className="mr-2 h-5 w-5" />
              Analysis Complete
            </>
          ) : (
            <>
              <Scan className="mr-2 h-5 w-5" />
              Analyze Chart
            </>
          )}
        </Button>
      </div>

      {quotaExhausted && (
        <div
          role="alert"
          className="mx-auto mt-6 flex w-full max-w-xl flex-col items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-4 text-center"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>{limitHeadline}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Clock className="h-3.5 w-3.5 text-gold" />
            <span className="uppercase tracking-wider">{countdownLabel}:</span>
            <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
              {formatCountdown(remainingMs)}
            </span>
          </div>
          {!isElite && (
            <a
              href="/pricing"
              className="text-xs font-semibold text-gold underline-offset-2 hover:underline"
            >
              View plans →
            </a>
          )}
        </div>
      )}

      {/* Signal Card, quick visual summary */}
      {report && !analyzing && (
        <section ref={resultsRef} className="space-y-3 scroll-mt-4">
          <h3 className="font-display text-lg font-semibold uppercase tracking-[0.18em] text-gold">
            Signal Summary
          </h3>
          <SignalCard report={report} asset={report.asset ?? "XAUUSD"} />
        </section>
      )}

      {/* Results */}
      {(analyzing || report) && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold uppercase tracking-[0.18em] text-gold">
              Analysis Report
            </h3>
            {analyzing && (
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-gold" />
                Analyzing chart…
              </span>
            )}
          </div>

          {analyzing ? (
            <ReportSkeleton />
          ) : report ? (
            <ReportView report={report} onConvertToSignal={handleConvertToSignal} />
          ) : null}
        </section>
      )}

      {/* Recent Analyses */}
      <HistoryPanel items={history} />
    </div>
  );
}
